import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { applicationsAPI, variantsAPI, workspacesAPI, type WorkspaceSummary } from '@/lib/api'
import { setActiveWorkspaceId } from '@/lib/workspace'
import type { Application, Variant } from '@/types'

interface AppContextValue {
  // Active workspace — resolved from /api/workspaces/me on boot.
  // Pre-auth this is always the migration workspace ("Demos and Tests").
  workspace: WorkspaceSummary | null
  workspaceId: string

  apps: Application[]
  selectedApp: Application | null
  selectedAppId: string

  // All variants across all apps (for building dropdowns)
  allVariants: Variant[]
  // Variants for the currently selected app
  variants: Variant[]
  selectedVariant: Variant | null
  selectedVariantId: string

  setSelectedAppId: (id: string) => void
  setSelectedVariantId: (id: string) => void

  refreshApps: () => Promise<Application[]>
  refreshVariants: () => Promise<Variant[]>
  /** Refetch a single Application by id and merge it into the local
   * `apps` state. Avoids the N+1 fan-out of `refreshApps()` when only
   * one app's fields changed (e.g., after a variant rescan auto-promotes
   * applicationSummary/Domain/UsageContext to the parent Application
   * doc — the change isn't visible until the cached app is refreshed). */
  refreshApp: (id: string) => Promise<Application | null>
}

const AppContext = createContext<AppContextValue | null>(null)

export function AppContextProvider({ children }: { children: ReactNode }) {
  // Workspace boot state. Children render only after `/me` resolves so
  // every downstream API call sees a valid workspaceId via lib/workspace.
  const [workspace, setWorkspace] = useState<WorkspaceSummary | null>(null)
  const [bootError, setBootError] = useState<string | null>(null)

  const [apps, setApps] = useState<Application[]>([])
  const [allVariants, setAllVariants] = useState<Variant[]>([])
  const [variants, setVariants] = useState<Variant[]>([])
  const [selectedAppId, setSelectedAppId] = useState(() => localStorage.getItem('selected-app-id') ?? '')
  const [selectedVariantId, setSelectedVariantId] = useState(() => localStorage.getItem('selected-variant-id') ?? '')

  const refreshApps = useCallback(async (): Promise<Application[]> => {
    const data = await applicationsAPI.getAll()
    setApps(data)
    // Load all variants for all apps (for building dropdowns)
    const allVars = await Promise.all(data.map((app) => variantsAPI.getByApp(app.id)))
    setAllVariants(allVars.flat())
    return data
  }, [])

  /** Targeted single-app refetch — used after rescan when the auto-
   * promote helper updated Application doc fields and we need the
   * cached `apps` entry to reflect them without paying the
   * `refreshApps()` fan-out. Returns null if the app no longer exists
   * (e.g., deleted concurrently); when that happens the stale entry
   * is removed from local `apps` state and selection is cleared if
   * the deleted app was currently selected — same shape as the
   * workspace-switch effect below. */
  const refreshApp = useCallback(async (id: string): Promise<Application | null> => {
    const updated = await applicationsAPI.getById(id)
    if (!updated) {
      setApps(prev => prev.filter(a => a.id !== id))
      if (selectedAppId === id) {
        setSelectedAppId('')
        setSelectedVariantId('')
      }
      return null
    }
    setApps(prev => prev.map(a => a.id === id ? updated : a))
    return updated
  }, [selectedAppId])

  const refreshVariants = useCallback(async (): Promise<Variant[]> => {
    if (!selectedAppId) {
      setVariants([])
      return []
    }
    const data = await variantsAPI.getByApp(selectedAppId)
    setVariants(data)
    return data
  }, [selectedAppId])

  // Listen to URL changes so switching workspaces (via the header
  // picker, or any /workspaces/:wid/... navigation) actually updates
  // the active workspace state — both the React context AND the
  // module-level activeWorkspaceId that lib/api uses to prefix every
  // workspace-scoped URL. Boot still happens via getMe() below for
  // the initial visit and any non-/workspaces/* route.
  const location = useLocation()
  // Resolve active workspace before any other API calls. Other useEffect
  // hooks below depend on `workspace` being set so the API client knows
  // its workspaceId prefix. If /me fails, surface the error and stop —
  // do not fall through to flat-URL calls that would 404.
  useEffect(() => {
    // Match the workspace id out of /workspaces/:wid/... — the
    // canonical scoped-route prefix. If we're on this kind of URL,
    // resolve via getById so the URL is the source of truth. If
    // we're on /onboarding, /account, /orgs/:id/settings, etc. (no
    // :wid in URL), fall back to getMe() to find a default.
    const m = location.pathname.match(/^\/workspaces\/([^/]+)/)
    const urlWid = m?.[1]

    // Cancellation guard. Without it, a slow getById/getMe resolution
    // from a prior effect run could land AFTER a newer run started
    // (e.g. user clicks two workspaces in quick succession) and
    // overwrite workspace + selection for the URL we just moved off.
    // Standard React-effect-with-async-work pattern.
    let cancelled = false

    if (urlWid && urlWid !== workspace?._id) {
      // Two cases land here:
      //   (a) Initial boot — `workspace` is null, the URL has a wid,
      //       and we haven't loaded the workspace yet. The user's
      //       persisted localStorage selection is exactly what we
      //       want to keep across a page refresh; the downstream
      //       apps-load and variants-load effects will validate and
      //       clear it only if it references something that doesn't
      //       exist in this workspace.
      //   (b) Active workspace switch — `workspace` is set to a
      //       different wid (user clicked a workspace in the header,
      //       or pasted a different /workspaces/:wid/... URL). Here
      //       we DO want to clear selection so the transition is
      //       immediate, not flashy.
      //
      // The two cases differ on `workspace !== null`; gate the clear
      // on that. Without the gate, every page refresh enters this
      // branch (workspace?._id is undefined on first render) and
      // wipes the user's persisted selection.
      const isActualSwitch = workspace !== null
      workspacesAPI.getById(urlWid)
        .then(ws => {
          if (cancelled) return
          setActiveWorkspaceId(ws._id)
          setWorkspace(ws)
          if (isActualSwitch) {
            setSelectedAppId('')
            setSelectedVariantId('')
          }
        })
        .catch(err => {
          if (cancelled) return
          const msg = err instanceof Error ? err.message : String(err)
          console.error('[useAppContext] workspace switch failed:', err)
          setBootError(`Failed to switch workspace: ${msg}`)
        })
      return () => { cancelled = true }
    }

    if (urlWid && urlWid === workspace?._id) {
      return () => { cancelled = true }  // already on the right one
    }

    // Non-workspace-scoped route AND no workspace yet — boot via getMe.
    if (!workspace) {
      workspacesAPI.getMe()
        .then(ws => {
          if (cancelled) return
          setActiveWorkspaceId(ws._id)
          setWorkspace(ws)
        })
        .catch(err => {
          if (cancelled) return
          const msg = err instanceof Error ? err.message : String(err)
          setBootError(`Failed to resolve active workspace: ${msg}`)
        })
    }
    return () => { cancelled = true }
  }, [location.pathname, workspace?._id])

  // Load apps on mount — gated on workspace having resolved. Errors
  // here previously fell through to a silent unhandled rejection,
  // leaving the user with a greyed Run button and no signal as to
  // why. Surfacing to console + bootError so next time it fails
  // it's debuggable.
  useEffect(() => {
    if (!workspace) return
    refreshApps()
      .then((data) => {
        // Clear stale selection if it references an app that no longer exists
        // (e.g., after a container rebuild that reseeded Mongo with new IDs)
        if (selectedAppId && !data.some((a) => a.id === selectedAppId)) {
          setSelectedAppId('')
          setSelectedVariantId('')
          return
        }
        // Auto-select the first app when nothing is selected (covers
        // first-time users + the post-logo-click "home" path where
        // the user expects a default app to be picked rather than
        // landing on a blank "Select app…" dropdown). localStorage
        // persistence (above) keeps the last-picked app sticky for
        // returning users; this fallback only fires when there's
        // genuinely no prior selection.
        if (data.length > 0 && !selectedAppId) {
          setSelectedAppId(data[0].id)
        }
      })
      .catch((err) => {
        const msg = err instanceof Error ? err.message : String(err)
        console.error('[useAppContext] applications load failed:', err)
        setBootError(`Failed to load applications: ${msg}`)
      })
  }, [workspace])

  // Load variants whenever the selected app changes. Selection
  // validation happens HERE, at load completion, rather than in a
  // standalone effect — that lets us distinguish "still loading
  // (don't clobber)" from "load resolved with N results (validate
  // now)." The standalone effect approach couldn't tell those apart
  // and skipped recovery when refreshVariants returned [], leaving
  // the user staring at "Variant is selected but not loaded yet"
  // when the truth was "this app has no variants."
  //
  // The workspace gate is critical: variantsAPI.getByApp goes
  // through apiFetch(${ws()}/...), which reads the active workspace
  // id from lib/workspace. On initial mount, selectedAppId comes
  // from localStorage immediately while workspacesAPI.getMe is
  // still in flight — without the gate, this effect fires first
  // with an unset workspace id and the variants response is
  // empty/wrong, leaving `variants` permanently out of sync with
  // `allVariants` (which is built by the workspace-gated refreshApps
  // and is correct). The header dropdown reads allVariants and
  // showed the data, but selectedVariant=variants.find(...) on the
  // runner page returned null — the "had to switch apps to recover"
  // symptom.
  useEffect(() => {
    if (!workspace || !selectedAppId) {
      setVariants([])
      return
    }
    // Cancellation guard: if the user switches apps (or any other
    // dep changes) while this fetch is in flight, a late resolution
    // or rejection would otherwise mutate state that's already moved
    // on to a different selection. The cleanup flips `cancelled`,
    // and the .then/.catch branches early-return if it's set —
    // standard React-effect-with-async-work pattern.
    let cancelled = false
    // Capture the selectedAppId at request-start time so the
    // recovery logic in .then can compare against the value the
    // request was actually for, not the current latest. Belt-and-
    // suspenders alongside the cancelled flag.
    const requestForAppId = selectedAppId
    refreshVariants()
      .then((vs) => {
        if (cancelled || requestForAppId !== selectedAppId) return
        // Empty result: clear stale id so the tooltip changes from
        // "variant not loaded" (misleading) to "select a variant"
        // (accurate). The header dropdown also reflects the empty
        // state, prompting the user to switch apps or create a
        // variant for this one.
        if (vs.length === 0) {
          if (selectedVariantId) setSelectedVariantId('')
          return
        }
        // Validate the current selection against the loaded list.
        // applicationId match is part of the predicate, not a gate
        // on the whole effect — handles mixed-app state cleanly.
        const currentValid = vs.some(
          v => v.id === selectedVariantId && v.applicationId === selectedAppId,
        )
        if (currentValid) return
        const firstForApp = vs.find(v => v.applicationId === selectedAppId)
        if (firstForApp) setSelectedVariantId(firstForApp.id)
      })
      .catch((err) => {
        // Variants failure isn't boot-fatal — apps may still be
        // selectable in the header — but log it so a greyed Run
        // button has a paper trail in the console.
        console.error('[useAppContext] variants load failed:', err)
        // Don't clobber selection on transient errors (network blip,
        // 5xx, expired session about to re-auth). A fetch rejection
        // is "unknown" — NOT "the variant is gone." Treating the two
        // the same destroyed live selection on every recoverable
        // failure (the symptom the user reported as "selected variant
        // disappears sometimes, can't pin down when"). The two
        // legitimate signals to clear — "variants empty" and
        // "current selection doesn't validate" — both live on the
        // .then path above; the user's next successful fetch
        // reconciles correctly.
      })
    return () => {
      cancelled = true
    }
  }, [workspace, selectedAppId, refreshVariants])

  // Persist selections
  useEffect(() => {
    localStorage.setItem('selected-app-id', selectedAppId)
  }, [selectedAppId])

  useEffect(() => {
    localStorage.setItem('selected-variant-id', selectedVariantId)
  }, [selectedVariantId])

  const selectedApp = apps.find((a) => a.id === selectedAppId) ?? null
  const selectedVariant = variants.find((v) => v.id === selectedVariantId) ?? null

  // (Auto-select moved into the variants-load effect above so it
  // runs only after variants finish loading — see the comment there
  // for why that ordering matters.)

  // Show a minimal boot/error state until /me resolves. Rendering the
  // children while workspace is null would let any of them issue a
  // workspace-scoped API call that would throw in lib/workspace.
  if (bootError) {
    return (
      <div style={{ padding: 24, fontFamily: 'system-ui' }}>
        <h2>Cannot finish loading the app</h2>
        <p>{bootError}</p>
        <p style={{ color: '#666' }}>
          A required boot API call failed. Check that the backend is
          up, that the boot migration completed (see server logs for{' '}
          <code>[workspace] migration complete</code>), and that your
          session is still valid (sign in again if you've been idle a
          while). Browser console has the full error.
        </p>
      </div>
    )
  }
  if (!workspace) {
    return null
  }

  return (
    <AppContext.Provider value={{
      workspace,
      workspaceId: workspace._id,
      apps,
      selectedApp,
      selectedAppId,
      allVariants,
      variants,
      selectedVariant,
      selectedVariantId,
      setSelectedAppId,
      setSelectedVariantId,
      refreshApps,
      refreshVariants,
      refreshApp,
    }}>
      {children}
    </AppContext.Provider>
  )
}

export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useAppContext must be used within AppContextProvider')
  return ctx
}

/**
 * Non-throwing variant of `useAppContext`. Returns `null` when called
 * outside an `AppContextProvider`. Use this from layout shells (Header,
 * Sidebar, PageLayout) that need to render BOTH under workspace-scoped
 * routes (where the provider is mounted) AND under identity-only routes
 * (e.g. `/admin/*`, #429 Wave 1 Track C) where it isn't.
 *
 * Workspace-scoped pages should keep using `useAppContext()` — they
 * never render without a provider and the throwing variant catches
 * mis-mounting as a loud crash instead of letting a `null` dereference
 * surface deeper as a confusing `Cannot read property of null`.
 *
 * The two-hook split (rather than changing `useAppContext` to return
 * `AppContextValue | null`) keeps the 20+ workspace-scoped consumers
 * unchanged — they correctly assume non-null context and would have
 * to grow null guards everywhere if the signature flipped. Only the
 * layout shells genuinely need the optional shape.
 */
export function useAppContextOptional(): AppContextValue | null {
  return useContext(AppContext)
}
